import supabase from '../supabase/supabase';

const requireOrganizationId = (organizationId) => {
  if (!organizationId) {
    throw new Error('Organization ID is required.');
  }
};

/*
 * ---------------------------------------------------------
 * GET CURRENT AUTH SESSION
 * ---------------------------------------------------------
 */
const getAuthSession = async () => {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) {
    console.error(
      '[Stripe Connect] Session error:',
      error
    );

    throw error;
  }

  if (!session?.access_token) {
    throw new Error(
      'Your session has expired. Please sign in again.'
    );
  }

  return session;
};

/*
 * ---------------------------------------------------------
 * EXTRACT EDGE FUNCTION ERROR
 * ---------------------------------------------------------
 */
const getFunctionErrorMessage = async (
  error,
  fallbackMessage
) => {
  console.error(
    '[Stripe Connect] Edge Function error:',
    error
  );

  /*
   * Supabase FunctionsHttpError can contain the actual
   * response returned by the Edge Function.
   */
  try {
    if (
      error?.context &&
      typeof error.context.json === 'function'
    ) {
      const body =
        await error.context.json();

      console.error(
        '[Stripe Connect] Edge Function response:',
        body
      );

      if (body?.message) {
        return body.message;
      }

      if (body?.error) {
        return typeof body.error === 'string'
          ? body.error
          : body.error?.message ||
              fallbackMessage;
      }
    }
  } catch (contextError) {
    console.error(
      '[Stripe Connect] Unable to read function error response:',
      contextError
    );
  }

  return (
    error?.message ||
    fallbackMessage
  );
};

/*
 * ---------------------------------------------------------
 * START STRIPE CONNECT ONBOARDING
 * ---------------------------------------------------------
 */
export const startStripeConnectOnboarding =
  async (organizationId) => {
    requireOrganizationId(
      organizationId
    );

    const session =
      await getAuthSession();

    console.log(
      '[Stripe Connect] Starting onboarding request:',
      {
        organizationId,
      }
    );

    const {
      data,
      error,
    } =
      await supabase.functions.invoke(
        'stripe-connect-onboarding',
        {
          body: {
            organizationId,
          },

          /*
           * Explicitly send the current JWT.
           */
          headers: {
            Authorization:
              `Bearer ${session.access_token}`,
          },
        }
      );

    if (error) {
      const message =
        await getFunctionErrorMessage(
          error,
          'Unable to start Stripe Connect onboarding.'
        );

      throw new Error(message);
    }

    console.log(
      '[Stripe Connect] Onboarding response received:',
      {
        success:
          data?.success,
        stripeAccountId:
          data?.stripeAccountId,
        onboardingComplete:
          data?.onboardingComplete,
        hasOnboardingUrl:
          Boolean(
            data?.onboardingUrl
          ),
      }
    );

    if (!data) {
      throw new Error(
        'Stripe Connect returned an empty response.'
      );
    }

    if (!data.success) {
      throw new Error(
        data.message ||
          'Unable to start Stripe Connect onboarding.'
      );
    }

    if (!data.onboardingUrl) {
      throw new Error(
        'Stripe did not return an onboarding URL.'
      );
    }

    return data;
  };

/*
 * ---------------------------------------------------------
 * STRIPE CONNECT SERVICE
 * ---------------------------------------------------------
 */
export const stripeConnectService = {
  /*
   * -------------------------------------------------------
   * GET CONNECT STATUS
   * -------------------------------------------------------
   */
  async getConnectAccount(
    organizationId
  ) {
    requireOrganizationId(
      organizationId
    );

    const session =
      await getAuthSession();

    const {
      data,
      error,
    } =
      await supabase.functions.invoke(
        'stripe-connect-status',
        {
          body: {
            organizationId,
          },

          headers: {
            Authorization:
              `Bearer ${session.access_token}`,
          },
        }
      );

    if (error) {
      const message =
        await getFunctionErrorMessage(
          error,
          'Unable to load Stripe Connect status.'
        );

      throw new Error(message);
    }

    if (!data) {
      throw new Error(
        'Stripe Connect status returned an empty response.'
      );
    }

    if (!data.success) {
      throw new Error(
        data.message ||
          'Unable to load Stripe Connect status.'
      );
    }

    return data;
  },

  /*
   * -------------------------------------------------------
   * CREATE ONBOARDING ACCOUNT LINK
   * -------------------------------------------------------
   */
  async createAccountLink(
    organizationId
  ) {
    const data =
      await startStripeConnectOnboarding(
        organizationId
      );

    return {
      ...data,

      /*
       * Billing.jsx supports both, but keeping `url`
       * provides a simple normalized property.
       */
      url:
        data.onboardingUrl,
    };
  },
};